/**
 * Combat: targeting, damage, death, the win condition, and superweapon firing
 * (R15, R16, R17, R30, R31).
 *
 * **Targeting reads the vision grid, not raw entity positions** (KTD7, R19).
 * That coupling is deliberate and it is why U12 precedes this unit: building
 * combat against raw positions and retrofitting fog gating afterwards produces
 * exactly the "unit shoots through fog" class the ordering exists to prevent.
 *
 * Two rules ride along here because they are destruction-driven and belong
 * nowhere else: the win condition (R30) and superweapon firing (R31). A fired
 * superweapon resolves as a large area effect through the ordinary damage path,
 * so it needs no bespoke damage code.
 */

import type { Ctx, Subsystem } from '../core/registry.js'
import * as fix from '../core/fixed.js'
import { MAP_SIZE, MAX_ENTITIES, MAX_PLAYERS, MATCH_TICK_CAP } from '../core/config.js'
import { World } from './world.js'
import { Entities, EF, ST, NO_ENTITY } from './entities.js'
import { Pathfind } from './pathfind.js'
import { Vision } from './vision.js'
import { Build } from './build.js'
import type { SimBridge } from './bridge.js'
import { CMD, REASON, type Command } from '../net/commands.js'
import { defById, ID, TAB, type EntityDef } from './data/entities.js'
import { weaponById, PROJECTILE, NO_WEAPON } from './data/weapons.js'
import { multiplierFor, resolveDamage } from './data/armor.js'

const MAX_PROJECTILES = 512
/** Cells per spatial-hash bucket. 8 gives 16×16 buckets on a 128-cell map. */
const BUCKET = 8
const BUCKETS_PER_SIDE = MAP_SIZE / BUCKET
const BUCKET_COUNT = BUCKETS_PER_SIDE * BUCKETS_PER_SIDE
/** Entities one bucket can hold before the overflow is simply not indexed. */
const BUCKET_CAPACITY = 96
/**
 * Prism Tank chaining.
 *
 * `CHAIN_SHARE` is a percentage of the firing tank's own damage that each
 * neighbour contributes, and the cap stops a deathball from scaling without
 * limit — a few tanks reinforcing each other is a reward for massing, twenty
 * multiplying it by twenty is a unit that ends the game on its own.
 *
 * The first numbers here (35% each, four deep) measurably broke the game.
 * A 180-match sweep put Helios at 66.7% against 33.3%, where the same sweep on
 * the previous build read 52.2/47.8 — so the tier itself was the cause, not
 * noise. The reason is that an army moves as a clump, which means chaining is
 * effectively *always on*, while the Ascendancy's piercing only pays when
 * enemies happen to line up. A conditional bonus that is never conditional is
 * just a bigger number.
 */
const CHAIN_RADIUS = fix.fromFloat(3.0)
const CHAIN_CELLS = 4
const CHAIN_SHARE = 20
const CHAIN_CAP = 3

/**
 * Veterancy: three ranks, each worth a flat damage bonus and a heal.
 *
 * Deliberately modest. A rank that doubles damage turns the first engagement
 * into the whole match, because whoever wins it fields units the loser cannot
 * answer. These numbers make a veteran clearly better without making a fresh
 * unit useless against one.
 */
/**
 * How much of a hit a dug-in squad shrugs off.
 *
 * Large enough to be worth the immobility — infantry in the open is the
 * cheapest thing on the map to kill, and half measures here would make digging
 * in a trap rather than a choice.
 */
const DUG_IN_REDUCTION = 45

const MAX_RANK = 3
const KILLS_PER_RANK = 3
const RANK_DAMAGE = 15
const RANK_HEAL = 25

/**
 * The repair vehicle's aura: cells of reach, and hp per tick.
 *
 * Two per tick is sixty a second — a Guardian Tank from half to full in about
 * five seconds parked next to one, slow enough that pulling a wounded tank
 * out of the line is a decision with a cost, not an oil change mid-fight.
 */
const REPAIR_VEHICLE_RANGE = 3
const REPAIR_VEHICLE_HEAL = 2

/**
 * High ground.
 *
 * The map has had cliffs, ramps and plateaus since the beginning and until now
 * they were scenery: nothing in combat or vision read the elevation, so a
 * three-level map played exactly like a flat one. This is the whole of the
 * mechanic — shooting *down* at somebody reaches further than shooting across.
 *
 * Only the higher unit gets anything. Penalising the one below as well would
 * count the same advantage twice and make a defended plateau unassailable; as
 * it stands the attacker's problem is that it has to close, which is the
 * problem high ground is supposed to create.
 *
 * Two levels is the cap because the map generator rarely stacks more, and an
 * uncapped bonus would turn the one four-level corner of a map into a fortress
 * nothing could answer.
 */
const HIGH_GROUND_RANGE = fix.fromFloat(0.18)
const MAX_HEIGHT_ADVANTAGE = 2

/** Half-width of an Arc Lancer's beam, in Q16.16 cells. */
const PIERCE_WIDTH = fix.fromFloat(0.55)

/** How far off-axis a turret may be and still fire, in BAM16 units. */
const FIRE_TOLERANCE = 2600

/**
 * Target-selection weights, in cells of distance a unit will trade for a better
 * target. Tuned against the bot sweep, where the failure they address is two
 * even armies spreading their fire and annihilating each other into a stalemate
 * neither can break.
 */
/** Full discount for a target at zero hit points. */
const FOCUS_WOUNDED = fix.fromFloat(5)
/** Per point of armor-matrix advantage over the neutral 1.0. */
const FOCUS_EFFECTIVE = fix.fromFloat(3)
/** Per ally already shooting at it. */
const FOCUS_ALLY = fix.fromFloat(0.6)
const FOCUS_ALLY_CAP = 4

/** No cell. Matches `pathfind`'s sentinel. */
const NO_CELL = -1

/** How close an aircraft must be to its airbase to count as landed, in cells. */
const LANDING_RANGE = fix.fromFloat(1.2)

/** How far beyond its weapon an attack-moving unit will look for something to close on. */
const CLOSE_IN_REACH = fix.fromFloat(2.5)
/** Ticks between close-in searches for one unit. */
const CLOSE_IN_INTERVAL = 20

/** Match status, mirrored from `sim/index.ts` to avoid a cyclic import. */
const MATCH_RUNNING = 0
const MATCH_WON = 1
const MATCH_STALLED = 2

export class Combat implements Subsystem {
  static id = 'combat'
  static deps = ['world', 'entities', 'pathfind', 'vision', 'build'] as const

  private ctx!: Ctx
  private world!: World
  private entities!: Entities
  private path!: Pathfind
  private vision!: Vision
  private build!: Build
  private bridge!: SimBridge

  /** Explicit attack orders outrank automatic acquisition until replaced. */
  private readonly ordered = new Uint8Array(MAX_ENTITIES)

  /**
   * The march an attack-mover paused to fight: the destination to resume once
   * nothing is left in range, or `NO_CELL`. Without stopping to engage, an
   * attack-moving army cruises straight through the battle, shooting over its
   * shoulder, and arrives at the far side half-dead.
   */
  private readonly resumeCell = new Int32Array(MAX_ENTITIES).fill(NO_CELL)
  /** Allies already shooting at each slot. Presentation of intent, not state. */
  private readonly incoming = new Int32Array(MAX_ENTITIES)

  // Projectiles in flight, as flat arrays so they hash like everything else.
  private readonly projX = new Int32Array(MAX_PROJECTILES)
  private readonly projY = new Int32Array(MAX_PROJECTILES)
  private readonly projTarget = new Uint32Array(MAX_PROJECTILES)
  private readonly projWeapon = new Int32Array(MAX_PROJECTILES)
  private readonly projOwner = new Int8Array(MAX_PROJECTILES)
  /**
   * Who fired it, as an **id** rather than a slot.
   *
   * A shell outlives its gun: the tank that fired can die, its slot be
   * recycled, and a raw slot index would then credit the kill to whatever new
   * unit moved in. The id is generation-checked, so a dead shooter simply
   * resolves to nothing.
   */
  private readonly projShooter = new Int32Array(MAX_PROJECTILES)
  private readonly projActive = new Uint8Array(MAX_PROJECTILES)
  private projCount = 0

  /** Per-player elimination flags, part of the hash. */
  readonly eliminated = new Uint8Array(MAX_PLAYERS)

  /**
   * A uniform spatial hash over entity positions, rebuilt each tick.
   *
   * Target acquisition without it is O(n²): every armed unit scanning every
   * entity is 160,000 comparisons per tick at four hundred units, and the
   * profiler measured 50 ms ticks against an 8 ms budget. Rebuilding is O(n)
   * and a query touches only the buckets a weapon can actually reach.
   *
   * Iteration inside a bucket is by insertion order, which is ascending slot
   * order because the rebuild walks slots ascending — so this introduces no
   * ordering nondeterminism.
   */
  private readonly bucketCount = new Int32Array(BUCKET_COUNT)
  private readonly bucketSlots = new Int32Array(BUCKET_COUNT * BUCKET_CAPACITY)
  /**
   * Set whenever an entity appears or disappears, and by every tick boundary.
   *
   * Commands are applied *before* subsystem `fixedUpdate` runs, so a
   * superweapon fired by a command — or a unit that finished building — would
   * otherwise be resolved against last tick's buckets. Rebuilding on demand
   * costs one O(n) pass and removes the whole class.
   */
  private hashDirty = true

  init(ctx: Ctx): void {
    this.ctx = ctx
    this.world = ctx.get<World>('world')
    this.entities = ctx.get<Entities>('entities')
    this.path = ctx.get<Pathfind>('pathfind')
    this.vision = ctx.get<Vision>('vision')
    this.build = ctx.get<Build>('build')
    this.bridge = ctx.get<SimBridge>('sim')

    ctx.hash.register('combat:ordered', this.ordered)
    ctx.hash.register('combat:resumeCell', this.resumeCell)
    ctx.hash.register('combat:projX', this.projX)
    ctx.hash.register('combat:projY', this.projY)
    ctx.hash.register('combat:projTarget', this.projTarget)
    ctx.hash.register('combat:projShooter', this.projShooter)
    ctx.hash.register('combat:projActive', this.projActive)
    ctx.hash.register('combat:eliminated', this.eliminated)

    ctx.on('actor:spawn', () => (this.hashDirty = true))
    ctx.on('actor:removed', (p) => {
      this.hashDirty = true
      this.resumeCell[(p as { slot: number }).slot] = NO_CELL
    })
  }

  // --- commands ------------------------------------------------------------

  handleCommand(cmd: Command): boolean {
    switch (cmd.t) {
      case CMD.Attack: {
        for (const id of cmd.units) {
          const slot = this.entities.resolve(id)
          if (slot < 0) continue
          this.entities.target[slot] = cmd.target
          this.ordered[slot] = 1
          this.entities.state[slot] = ST.Attacking
        }
        return true
      }
      case CMD.Guard: {
        for (const id of cmd.units) {
          const slot = this.entities.resolve(id)
          if (slot < 0) continue
          this.entities.state[slot] = ST.Guarding
          this.entities.target[slot] = NO_ENTITY
          this.ordered[slot] = 0
        }
        return true
      }
      case CMD.SuperFire:
        this.fireSuperweapon(cmd.slot, cmd.cx, cmd.cy)
        return true
      default:
        return false
    }
  }

  // --- per-tick ------------------------------------------------------------

  fixedUpdate(tick: number): void {
    this.hashDirty = true
    this.rebuildSpatialHash()
    this.updateProjectiles()
    this.updateShooters()
    this.updateRepairVehicles()
    this.checkMatchEnd(tick)
  }

  private rebuildSpatialHash(): void {
    this.hashDirty = false
    this.bucketCount.fill(0)
    const limit = this.entities.limit
    for (let slot = 0; slot < limit; slot++) {
      if (!this.entities.isAlive(slot)) continue
      const cx = this.entities.x[slot] >> 16
      const cy = this.entities.y[slot] >> 16
      if (cx < 0 || cy < 0 || cx >= MAP_SIZE || cy >= MAP_SIZE) continue
      const bucket = ((cy / BUCKET) | 0) * BUCKETS_PER_SIDE + ((cx / BUCKET) | 0)
      const n = this.bucketCount[bucket]
      if (n >= BUCKET_CAPACITY) continue
      this.bucketSlots[bucket * BUCKET_CAPACITY + n] = slot
      this.bucketCount[bucket] = n + 1
    }
  }

  /**
   * Visit every entity whose bucket overlaps a radius around a point.
   * The callback may see entities slightly outside the radius; the caller
   * still range-checks.
   */
  private forEachNear(
    cx: number,
    cy: number,
    radiusCells: number,
    fn: (slot: number) => void,
  ): void {
    if (this.hashDirty) this.rebuildSpatialHash()
    const minBx = Math.max(0, ((cx - radiusCells) / BUCKET) | 0)
    const maxBx = Math.min(BUCKETS_PER_SIDE - 1, ((cx + radiusCells) / BUCKET) | 0)
    const minBy = Math.max(0, ((cy - radiusCells) / BUCKET) | 0)
    const maxBy = Math.min(BUCKETS_PER_SIDE - 1, ((cy + radiusCells) / BUCKET) | 0)
    for (let by = minBy; by <= maxBy; by++) {
      for (let bx = minBx; bx <= maxBx; bx++) {
        const bucket = by * BUCKETS_PER_SIDE + bx
        const n = this.bucketCount[bucket]
        const base = bucket * BUCKET_CAPACITY
        for (let i = 0; i < n; i++) fn(this.bucketSlots[base + i])
      }
    }
  }

  /**
   * How many of this side's units are already shooting at each slot, from last
   * tick's targets. Rebuilt once per tick rather than maintained incrementally:
   * a full pass is a few thousand array reads, and an incremental count is one
   * more thing that can drift out of step with the entity table.
   */
  private countIncoming(): void {
    this.incoming.fill(0)
    const limit = this.entities.limit
    for (let slot = 0; slot < limit; slot++) {
      if (!this.entities.isAlive(slot)) continue
      const target = this.entities.target[slot]
      if (target === NO_ENTITY) continue
      const t = this.entities.resolve(target)
      if (t >= 0) this.incoming[t]++
    }
  }

  private updateShooters(): void {
    this.countIncoming()
    const limit = this.entities.limit
    for (let slot = 0; slot < limit; slot++) {
      if (!this.entities.isAlive(slot)) continue
      if (this.entities.cooldown[slot] > 0) this.entities.cooldown[slot]--

      const def = defById(this.entities.kind[slot])
      if (!def) continue
      // A transport fights with its passenger's weapon. Read here rather than
      // baked in at load time so that a mid-match change — a passenger being
      // put down, a new one climbing in — takes effect on the same tick.
      const weaponId = this.weaponOf(slot, def)
      if (weaponId === NO_WEAPON) continue
      if (def.isAir && this.updateAircraft(slot, def)) continue
      // An offline defence is inert. That is the whole point of the power
      // penalty, and it is why the HUD has to say so loudly.
      if (this.entities.has(slot, EF.Offline)) continue
      if (this.entities.has(slot, EF.Building)) continue

      const weapon = weaponById(weaponId)
      if (!weapon || weapon.range <= 0) continue
      const owner = this.entities.owner[slot]

      let target = this.resolveTarget(slot, owner, weapon.range)
      if (target === NO_ENTITY) {
        // Nothing in range. An attack-moving unit that has arrived looks
        // *beyond* its weapon and walks the rest of the way.
        //
        // Without this, "attack move" means "shoot whatever happens to already
        // be in range", and an army that stops six cells from a base with a
        // five-and-a-half-cell gun stands there indefinitely. Probing stalled
        // matches found exactly that: seventeen units attack-moving, closest
        // approach 5.8 cells, nothing being shot at.
        const state = this.entities.state[slot]
        // A paused attack-mover with nothing left to shoot picks its march
        // back up. This outranks closeIn: the march will walk it into the
        // next fight anyway, and it is what the player actually ordered.
        if (state === ST.AttackMoving && this.path.goal[slot] < 0 && this.resumeCell[slot] >= 0) {
          const cell = this.resumeCell[slot]
          this.resumeCell[slot] = NO_CELL
          this.path.orderGroup([this.entities.idAt(slot)], cell, ST.AttackMoving)
          continue
        }
        if ((state === ST.AttackMoving || state === ST.Idle) && this.path.goal[slot] < 0) {
          this.closeIn(slot, owner, weapon.range)
        }
        if (this.entities.state[slot] === ST.Attacking && this.path.goal[slot] < 0) {
          this.entities.state[slot] = ST.Idle
        }
        continue
      }

      const tSlot = this.entities.resolve(target)
      if (tSlot < 0) {
        this.entities.target[slot] = NO_ENTITY
        this.ordered[slot] = 0
        continue
      }

      const dx = fix.sub(fix.raw(this.entities.x[tSlot]), fix.raw(this.entities.x[slot]))
      const dy = fix.sub(fix.raw(this.entities.y[tSlot]), fix.raw(this.entities.y[slot]))
      const dist = fix.length(dx, dy)

      // Rotate the turret toward the target. Firing before it lines up is what
      // makes tanks read as vehicles rather than as turrets on wheels.
      const desired = fix.atan2(dy, dx)
      this.entities.turret[slot] = fix.turnToward(
        this.entities.turret[slot],
        desired,
        weapon.turnRate,
      )

      // A minimum range is a hole in the middle of the weapon, not a shorter
      // reach: artillery with something standing on top of it is defenceless,
      // which is why it needs an escort.
      if (def.minRange !== undefined && dist < def.minRange) {
        if (this.entities.state[slot] !== ST.AttackMoving) this.entities.state[slot] = ST.Attacking
        continue
      }

      if (dist > this.reach(weapon.range, slot, tSlot)) {
        // An explicit attack order on an out-of-range target moves into range.
        if (this.ordered[slot] === 1 && this.entities.has(slot, EF.Mobile)) {
          // Snapped to the coarse lattice so an engagement shares flow fields
          // instead of computing one per unit per tick.
          const cell = this.path.coarseDestination(
            this.world.cellAt(fix.raw(this.entities.x[tSlot]), fix.raw(this.entities.y[tSlot])),
          )
          if (cell >= 0 && this.path.goal[slot] !== cell) {
            this.path.orderGroup([this.entities.idAt(slot)], cell, ST.Attacking)
            this.entities.state[slot] = ST.Attacking
          }
        }
        continue
      }

      // In range: stop closing, and fire when aligned and off cooldown.
      if (this.ordered[slot] === 1 && this.path.goal[slot] >= 0) this.path.stop(slot)
      // An attack-mover halts to fight and remembers where it was going; the
      // march resumes once nothing is left in range. Aircraft are exempt —
      // their engagement rhythm is the rearm cycle, not the march.
      if (
        !def.isAir &&
        this.entities.state[slot] === ST.AttackMoving &&
        this.path.goal[slot] >= 0
      ) {
        this.resumeCell[slot] = this.path.goal[slot]
        this.path.pause(slot)
      }
      if (this.entities.state[slot] !== ST.AttackMoving) this.entities.state[slot] = ST.Attacking
      if (this.entities.cooldown[slot] > 0) continue
      if (Math.abs(fix.angleDiff(this.entities.turret[slot], desired)) > FIRE_TOLERANCE) continue

      this.fire(slot, tSlot, weaponId)
    }
  }

  /**
   * Walk the last stretch to something just out of range.
   *
   * Rate-limited by slot so that an army arriving together does not request a
   * flow field per unit per tick — the failure that once put the simulation at
   * 35 ms a tick. Units converging on the same structure share one field,
   * because fields are cached by destination.
   */
  private closeIn(slot: number, owner: number, range: fix.Fixed): void {
    if ((this.ctx.time.tick + slot) % CLOSE_IN_INTERVAL !== 0) return
    const reach = fix.mul(range, CLOSE_IN_REACH)
    const target = this.acquire(slot, owner, reach)
    if (target === NO_ENTITY) return
    const tSlot = this.entities.resolve(target)
    if (tSlot < 0) return
    const cell = this.world.cellAt(fix.raw(this.entities.x[tSlot]), fix.raw(this.entities.y[tSlot]))
    if (cell < 0) return
    this.path.orderGroup([this.entities.idAt(slot)], cell, ST.AttackMoving)
  }

  /**
   * The rearm cycle: fly home empty, park, refill, fly again.
   *
   * Returns true when the aircraft is busy with the cycle and must not shoot.
   *
   * The trip home is the whole balance of the unit. A gunship that ignores
   * terrain, outruns everything, and hits for four times a tank's damage is
   * only fair because a third of its life is spent unarmed and in transit — so
   * this state runs *before* target acquisition, not alongside it.
   */
  private updateAircraft(slot: number, def: EntityDef): boolean {
    const state = this.entities.state[slot]

    if (state === ST.Rearming) {
      if (this.entities.rearm[slot] > 0) this.entities.rearm[slot]--
      if (this.entities.rearm[slot] > 0) return true
      this.entities.ammo[slot] = def.ordnance ?? 1
      this.entities.state[slot] = ST.Idle
      this.ctx.emit('air:rearmed', { owner: this.entities.owner[slot], id: this.entities.idAt(slot) })
      return true
    }

    if (state === ST.RearmingInbound) {
      const home = this.homeCell(slot, def)
      if (home < 0) {
        // Its airbase is gone. It stays up, empty, and answers move orders —
        // better than a unit that vanishes or one that hunts with no ammunition.
        this.entities.state[slot] = ST.Idle
        return true
      }
      if (this.path.goal[slot] === NO_CELL) {
        const dx = fix.sub(this.world.cellCenterX(home % MAP_SIZE), fix.raw(this.entities.x[slot]))
        const dy = fix.sub(
          this.world.cellCenterY((home / MAP_SIZE) | 0),
          fix.raw(this.entities.y[slot]),
        )
        if (fix.length(dx, dy) <= LANDING_RANGE) {
          this.entities.state[slot] = ST.Rearming
          this.entities.rearm[slot] = def.rearmTime ?? 300
        } else {
          this.path.orderAircraft(slot, home, ST.RearmingInbound)
        }
      }
      return true
    }

    if (this.entities.ammo[slot] <= 0) {
      this.entities.target[slot] = NO_ENTITY
      this.ordered[slot] = 0
      this.entities.state[slot] = ST.RearmingInbound
      const home = this.homeCell(slot, def)
      if (home >= 0) this.path.orderAircraft(slot, home, ST.RearmingInbound)
      return true
    }

    return false
  }

  /** The cell of this aircraft's nearest owned airbase, or -1 if it has none. */
  private homeCell(slot: number, def: EntityDef): number {
    const owner = this.entities.owner[slot]
    const wanted = def.homeStructure
    let best = -1
    let bestDist = Infinity
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s)) continue
      if (this.entities.owner[s] !== owner) continue
      if (this.entities.has(s, EF.Building)) continue
      if (this.entities.kind[s] !== wanted) continue
      const dx = fix.toFloat(fix.sub(fix.raw(this.entities.x[s]), fix.raw(this.entities.x[slot])))
      const dy = fix.toFloat(fix.sub(fix.raw(this.entities.y[s]), fix.raw(this.entities.y[slot])))
      const d = dx * dx + dy * dy
      if (d < bestDist) {
        bestDist = d
        best = this.world.cellAt(fix.raw(this.entities.x[s]), fix.raw(this.entities.y[s]))
      }
    }
    return best
  }

  /**
   * The current target: an explicit order if it is still valid, otherwise
   * automatic acquisition.
   *
   * An explicit attack order **outranks** acquisition and is not overridden by
   * a nearer opportunistic target. Losing that would make focus-fire impossible.
   */
  private resolveTarget(slot: number, owner: number, range: fix.Fixed): number {
    if (this.ordered[slot] === 1) {
      const t = this.entities.target[slot]
      const tSlot = this.entities.resolve(t)
      if (tSlot >= 0 && this.vision.canSee(owner, tSlot)) return t
      this.ordered[slot] = 0
      this.entities.target[slot] = NO_ENTITY
    }

    const state = this.entities.state[slot]
    const acquires =
      state === ST.Idle ||
      state === ST.AttackMoving ||
      state === ST.Guarding ||
      state === ST.Attacking
    if (!acquires) return NO_ENTITY

    return this.acquire(slot, owner, range)
  }

  /**
   * Nearest enemy within range that the owner can actually see, preferring one
   * already damaged so focus fire emerges without micromanagement.
   */
  private acquire(slot: number, owner: number, range: fix.Fixed): number {
    const shooter = defById(this.entities.kind[slot])
    const warheadOf = weaponById(shooter?.weapon ?? NO_WEAPON)?.warhead ?? 0
    const sx = fix.raw(this.entities.x[slot])
    const sy = fix.raw(this.entities.y[slot])
    let best = NO_ENTITY
    let bestScore = Infinity

    // Sized to the *widest* the reach could be from anywhere, since whether a
    // given candidate is in range depends on how far below the shooter it is.
    const radiusCells = (this.maxReach(range) >> 16) + 1
    this.forEachNear(sx >> 16, sy >> 16, radiusCells, (t) => {
      if (t === slot) return
      if (!this.entities.isAlive(t)) return
      const tOwner = this.entities.owner[t]
      if (tOwner < 0 || tOwner === owner) return
      if (this.entities.has(t, EF.Building)) return
      // The fog gate. An enemy in explored-but-not-visible territory is not a
      // target, however precisely the simulation knows where it is.
      if (!this.vision.canSee(owner, t)) return

      const dx = fix.sub(fix.raw(this.entities.x[t]), sx)
      const dy = fix.sub(fix.raw(this.entities.y[t]), sy)
      const dist = fix.length(dx, dy)
      if (dist > this.reach(range, slot, t)) return

      // Target selection is scored in cells, as a discount off the distance:
      // "I would walk this much further for this target".
      //
      // The three terms are what turns a mush of independent duels into an
      // engagement that someone wins. Two even armies that each spread their
      // fire annihilate each other and neither side is left able to push — the
      // exact shape of every bot match that ran to the tick cap. Concentrated
      // fire removes shooters from the other side sooner, and the side with the
      // small advantage compounds it instead of trading evenly.
      let discount = 0

      // 1. Finish what is nearly dead. Scaled by how nearly, not a flat bonus:
      //    a unit at 10% is worth crossing the battlefield for, one at 90% is
      //    not.
      const maxHp = this.entities.maxHp[t]
      if (maxHp > 0) {
        const missing = fix.div(
          fix.fromInt(maxHp - this.entities.hp[t]),
          fix.fromInt(maxHp),
        )
        discount = fix.add(discount as fix.Fixed, fix.mul(missing, FOCUS_WOUNDED))
      }

      // 2. Shoot what this weapon is *for*. Without it a tank will happily
      //    empty itself into infantry its shells barely scratch while the
      //    enemy armour rolls past.
      const tDef = defById(this.entities.kind[t])
      if (tDef) {
        const effect = fix.sub(multiplierFor(warheadOf, tDef.armor), fix.ONE)
        discount = fix.add(discount as fix.Fixed, fix.mul(effect, FOCUS_EFFECTIVE))
      }

      // 3. Join what the rest of the army is already shooting, up to a cap so
      //    that a whole company does not chase one scout across the map.
      const allies = this.incoming[t]
      if (allies > 0) {
        const capped = Math.min(allies, FOCUS_ALLY_CAP)
        discount = fix.add(discount as fix.Fixed, fix.mul(fix.fromInt(capped), FOCUS_ALLY))
      }

      const score = fix.sub(dist, discount as fix.Fixed)
      if (score < bestScore) {
        bestScore = score
        best = this.entities.idAt(t)
      }
    })
    return best
  }

  /**
   * The weapon an entity is actually firing.
   *
   * Its own, unless it is carrying somebody — a transport takes its
   * passenger's weapon outright, which is the whole unit: the same chassis is
   * a scout, a sniper on wheels or a repair vehicle depending on who is
   * inside, and the player composes it rather than picking it off a list.
   */
  private weaponOf(slot: number, def: EntityDef): number {
    if (!def.carriesInfantry) return def.weapon
    const rider = this.entities.passenger[slot]
    if (rider === 0) return def.weapon
    const riderDef = defById(rider)
    // An engineer aboard disarms the vehicle: it is a repair vehicle now, and
    // a repair vehicle that also lobs demolition charges is a different and
    // sillier unit. The healing happens in `updateRepairVehicles`.
    if (riderDef?.isEngineer) return NO_WEAPON
    const carried = riderDef?.weapon ?? NO_WEAPON
    return carried === NO_WEAPON ? def.weapon : carried
  }

  /**
   * A transport carrying an engineer mends nearby friendly vehicles (R13's
   * composable-transport idea completed: the chassis was always meant to be a
   * repair vehicle with the right passenger).
   *
   * An aura rather than a targeted beam: it needs no target resolution, no
   * projectile, and no orders — park it behind the line and it works, which
   * is also what makes it simple enough to trust in lockstep. Vehicles only:
   * engineers fix machines, and healing infantry is a different unit's job.
   * It does not heal itself, so a pair of them cannot make each other
   * immortal — and structures are the walking engineer's trade instead.
   */
  private updateRepairVehicles(): void {
    const limit = this.entities.limit
    for (let slot = 0; slot < limit; slot++) {
      if (!this.entities.isAlive(slot)) continue
      const def = defById(this.entities.kind[slot])
      if (!def?.carriesInfantry) continue
      if (!defById(this.entities.passenger[slot])?.isEngineer) continue

      const owner = this.entities.owner[slot]
      const cx = this.entities.x[slot] >> 16
      const cy = this.entities.y[slot] >> 16
      for (let other = 0; other < limit; other++) {
        if (other === slot || !this.entities.isAlive(other)) continue
        if (this.entities.owner[other] !== owner) continue
        if (this.entities.hp[other] >= this.entities.maxHp[other]) continue
        const otherDef = defById(this.entities.kind[other])
        if (otherDef?.isStructure || otherDef?.tab !== TAB.Vehicle) continue
        const dx = Math.abs((this.entities.x[other] >> 16) - cx)
        const dy = Math.abs((this.entities.y[other] >> 16) - cy)
        if (Math.max(dx, dy) > REPAIR_VEHICLE_RANGE) continue
        this.entities.hp[other] = Math.min(
          this.entities.maxHp[other],
          this.entities.hp[other] + REPAIR_VEHICLE_HEAL,
        )
      }
    }
  }

  /** The elevation level of the cell an entity is standing in. */
  private levelOf(slot: number): number {
    return this.world.levelAt(
      fix.raw(this.entities.x[slot]) >> 16,
      fix.raw(this.entities.y[slot]) >> 16,
    )
  }

  /**
   * A weapon's reach from `slot` against `target`, with the high-ground bonus.
   *
   * Computed per target rather than once per shooter, because the advantage is
   * a *difference*: the same tank on the same hill reaches further against
   * something in the valley than against something beside it.
   */
  private reach(base: fix.Fixed, slot: number, target: number): fix.Fixed {
    const advantage = Math.min(
      MAX_HEIGHT_ADVANTAGE,
      Math.max(0, this.levelOf(slot) - this.levelOf(target)),
    )
    if (advantage === 0) return base
    return fix.add(base, fix.mul(base, fix.mul(fix.fromInt(advantage), HIGH_GROUND_RANGE)))
  }

  /** The furthest that reach could possibly stretch, for sizing a search. */
  private maxReach(base: fix.Fixed): fix.Fixed {
    return fix.add(
      base,
      fix.mul(base, fix.mul(fix.fromInt(MAX_HEIGHT_ADVANTAGE), HIGH_GROUND_RANGE)),
    )
  }

  private fire(slot: number, targetSlot: number, weaponId: number): void {
    const firing = defById(this.entities.kind[slot])
    if (firing?.isAir && --this.entities.ammo[slot] <= 0) {
      // Turn for home on the same tick as the last shot. Leaving it in an
      // attack state until the next tick is a window where an empty aircraft
      // still reads as a threat, and the state is what the HUD shows.
      this.path.stop(slot)
      this.entities.target[slot] = NO_ENTITY
      this.ordered[slot] = 0
      this.entities.state[slot] = ST.RearmingInbound
    }
    const weapon = weaponById(weaponId)
    if (!weapon) return
    this.entities.cooldown[slot] = weapon.cooldown

    this.ctx.emit('weapon:fire', {
      shooter: this.entities.idAt(slot),
      // Carried so the renderer can colour a beam by whose it is. Event
      // payloads are not hashed, so this adds nothing to simulation state.
      owner: this.entities.owner[slot],
      weapon: weaponId,
      x: this.entities.x[slot],
      y: this.entities.y[slot],
      tx: this.entities.x[targetSlot],
      ty: this.entities.y[targetSlot],
    })

    if (weapon.projectile === PROJECTILE.Ballistic) {
      this.spawnProjectile(slot, targetSlot, weaponId)
      return
    }

    // Tier-three beams resolve differently from an ordinary instant weapon.
    const kind = this.entities.kind[slot]
    if (kind === ID.PrismTank) {
      this.fireChained(slot, targetSlot, weaponId)
      return
    }
    if (kind === ID.ArcLancer) {
      this.firePiercing(slot, targetSlot, weaponId)
      return
    }

    this.applyWeapon(
      weaponId,
      this.entities.owner[slot],
      this.entities.idAt(targetSlot),
      fix.raw(this.entities.x[targetSlot]),
      fix.raw(this.entities.y[targetSlot]),
      slot,
    )
  }

  /**
   * A Prism Tank's shot, fed by the Prism Tanks around it.
   *
   * Every friendly Prism Tank within `CHAIN_RADIUS` that is not itself firing
   * this tick adds a share of its own damage to the shot. Massing them is
   * therefore worth more than spreading them, which is the opposite of what
   * splash weapons teach and the reason the unit is interesting.
   *
   * Contributors are collected in slot order and the bonus is integer
   * arithmetic, so two clients agree exactly on the damage.
   */
  private fireChained(slot: number, targetSlot: number, weaponId: number): void {
    const weapon = weaponById(weaponId)
    if (!weapon) return
    const owner = this.entities.owner[slot]

    const links: number[] = []
    this.forEachNear(
      this.entities.x[slot] >> 16,
      this.entities.y[slot] >> 16,
      CHAIN_CELLS,
      (other) => {
        if (other === slot || links.length >= CHAIN_CAP) return
        if (!this.entities.isAlive(other)) return
        if (this.entities.owner[other] !== owner) return
        if (this.entities.kind[other] !== ID.PrismTank) return
        const dx = fix.sub(fix.raw(this.entities.x[other]), fix.raw(this.entities.x[slot]))
        const dy = fix.sub(fix.raw(this.entities.y[other]), fix.raw(this.entities.y[slot]))
        if (fix.length(dx, dy) > CHAIN_RADIUS) return
        links.push(other)
      },
    )

    const damage = weapon.damage + Math.floor((weapon.damage * CHAIN_SHARE * links.length) / 100)
    // The renderer draws a beam from each contributor into the firing tank, so
    // a charged shot is visibly different from a lone one before it lands.
    if (links.length > 0) {
      this.ctx.emit('weapon:chain', {
        weapon: weaponId,
        owner,
        x: this.entities.x[slot],
        y: this.entities.y[slot],
        links: links.map((s) => ({ x: this.entities.x[s], y: this.entities.y[s] })),
      })
    }

    this.ctx.emit('weapon:impact', {
      weapon: weaponId,
      x: this.entities.x[targetSlot],
      y: this.entities.y[targetSlot],
    })
    this.damage(targetSlot, damage, weapon.warhead, owner, slot)
  }

  /**
   * An Arc Lancer's shot, which does not stop at its target.
   *
   * Everything hostile within half a cell of the line from the Lancer to its
   * target takes the hit. Positioning is the whole weapon: catch a column and
   * it is devastating, catch a single scout and it is a slow, expensive rifle.
   */
  private firePiercing(slot: number, targetSlot: number, weaponId: number): void {
    const weapon = weaponById(weaponId)
    if (!weapon) return
    const owner = this.entities.owner[slot]
    const sx = fix.raw(this.entities.x[slot])
    const sy = fix.raw(this.entities.y[slot])
    const tx = fix.raw(this.entities.x[targetSlot])
    const ty = fix.raw(this.entities.y[targetSlot])
    const dx = fix.sub(tx, sx)
    const dy = fix.sub(ty, sy)
    const span = fix.length(dx, dy)

    // Collected before applying: `damage` can kill, and a death reorders the
    // spatial buckets underneath the walk.
    const hit: number[] = []
    const reach = (span >> 16) + 2
    this.forEachNear(sx >> 16, sy >> 16, reach, (other) => {
      if (!this.entities.isAlive(other)) return
      if (this.entities.owner[other] === owner) return
      const ox = fix.sub(fix.raw(this.entities.x[other]), sx)
      const oy = fix.sub(fix.raw(this.entities.y[other]), sy)
      // Distance from the point to the segment, in fixed point. Anything past
      // the target is behind the beam and is not hit.
      const along = span > 0 ? fix.div(fix.add(fix.mul(ox, dx), fix.mul(oy, dy)), span) : fix.ZERO
      if (along < 0 || along > span) return
      const perp = span > 0 ? fix.div(fix.sub(fix.mul(ox, dy), fix.mul(oy, dx)), span) : fix.ZERO
      if (Math.abs(perp) > PIERCE_WIDTH) return
      hit.push(other)
    })

    this.ctx.emit('weapon:impact', { weapon: weaponId, x: tx, y: ty })
    for (const other of hit) this.damage(other, weapon.damage, weapon.warhead, owner, slot)
  }

  private spawnProjectile(slot: number, targetSlot: number, weaponId: number): void {
    for (let i = 0; i < MAX_PROJECTILES; i++) {
      if (this.projActive[i]) continue
      this.projX[i] = this.entities.x[slot]
      this.projY[i] = this.entities.y[slot]
      // The **id**, not the slot. A projectile in flight toward a unit that
      // dies and whose slot is recycled must not damage the new occupant.
      this.projTarget[i] = this.entities.idAt(targetSlot)
      this.projWeapon[i] = weaponId
      this.projOwner[i] = this.entities.owner[slot]
      this.projShooter[i] = this.entities.idAt(slot)
      this.projActive[i] = 1
      this.projCount++
      return
    }
  }

  private updateProjectiles(): void {
    for (let i = 0; i < MAX_PROJECTILES; i++) {
      if (!this.projActive[i]) continue
      const weapon = weaponById(this.projWeapon[i])
      if (!weapon) {
        this.projActive[i] = 0
        continue
      }
      const tSlot = this.entities.resolve(this.projTarget[i])
      if (tSlot < 0) {
        // The target died en route. The shell simply fizzles rather than
        // finding whoever inherited the slot.
        this.projActive[i] = 0
        this.projCount--
        continue
      }

      const dx = fix.sub(fix.raw(this.entities.x[tSlot]), fix.raw(this.projX[i]))
      const dy = fix.sub(fix.raw(this.entities.y[tSlot]), fix.raw(this.projY[i]))
      const dist = fix.length(dx, dy)

      if (dist <= weapon.projectileSpeed) {
        this.projActive[i] = 0
        this.projCount--
        this.applyWeapon(
          this.projWeapon[i],
          this.projOwner[i],
          this.projTarget[i],
          fix.raw(this.entities.x[tSlot]),
          fix.raw(this.entities.y[tSlot]),
          this.entities.resolve(this.projShooter[i]),
        )
        continue
      }
      this.projX[i] = fix.add(
        fix.raw(this.projX[i]),
        fix.div(fix.mul(dx, weapon.projectileSpeed), dist),
      )
      this.projY[i] = fix.add(
        fix.raw(this.projY[i]),
        fix.div(fix.mul(dy, weapon.projectileSpeed), dist),
      )
    }
  }

  /** Apply a weapon's damage, single-target or splash, through the armor matrix. */
  private applyWeapon(
    weaponId: number,
    attacker: number,
    targetId: number,
    x: fix.Fixed,
    y: fix.Fixed,
    /**
     * The firing entity's slot, for rank credit. Negative when it is gone —
     * a shell that lands after its tank dies still does damage, and nobody is
     * promoted for it.
     */
    shooter = -1,
  ): void {
    const weapon = weaponById(weaponId)
    if (!weapon) return
    this.ctx.emit('weapon:impact', { weapon: weaponId, x, y })

    if (weapon.splash <= 0) {
      const slot = this.entities.resolve(targetId)
      if (slot >= 0) this.damage(slot, weapon.damage, weapon.warhead, attacker, shooter)
      return
    }

    // Splash hits **every** entity in radius exactly once, including the
    // attacker's own units. Friendly fire is a real cost of a siege weapon.
    //
    // Collected before applying, because `damage` can kill and therefore
    // reorder the buckets underneath the walk.
    const hit: number[] = []
    this.forEachNear(x >> 16, y >> 16, (weapon.splash >> 16) + 1, (s) => {
      if (!this.entities.isAlive(s)) return
      const dx = fix.sub(fix.raw(this.entities.x[s]), x)
      const dy = fix.sub(fix.raw(this.entities.y[s]), y)
      if (fix.length(dx, dy) > weapon.splash) return
      hit.push(s)
    })
    for (const s of hit) this.damage(s, weapon.damage, weapon.warhead, attacker, shooter)
  }

  /** Apply damage to a slot, killing it if it drops to zero. */
  damage(slot: number, baseDamage: number, warhead: number, attacker: number, shooter = -1): void {
    if (!this.entities.isAlive(slot)) return
    const def = defById(this.entities.kind[slot])
    if (!def) return
    const scaled = shooter >= 0 ? this.withRank(shooter, baseDamage) : baseDamage
    let dealt = resolveDamage(scaled, warhead, def.armor)
    if (this.entities.has(slot, EF.DugIn)) {
      // Integer arithmetic throughout. A fractional hit point is a float in
      // the simulation, and two clients would round it differently.
      dealt -= Math.floor((dealt * DUG_IN_REDUCTION) / 100)
    }
    this.entities.hp[slot] -= dealt
    this.ctx.emit('actor:damaged', {
      id: this.entities.idAt(slot),
      attacker,
      amount: dealt,
    })
    if (this.entities.hp[slot] <= 0) {
      if (shooter >= 0) this.creditKill(shooter)
      this.kill(slot)
    }
  }

  /**
   * Damage after the shooter's rank bonus.
   *
   * Integer arithmetic, because a fractional hit point is a float in the
   * simulation and two clients would round it differently.
   */
  private withRank(shooter: number, damage: number): number {
    if (!this.entities.isAlive(shooter)) return damage
    const rank = this.entities.rank[shooter]
    if (rank <= 0) return damage
    return damage + Math.floor((damage * RANK_DAMAGE * rank) / 100)
  }

  /**
   * A unit that kills something gets closer to its next rank.
   *
   * Veterancy is the cheapest source of attachment a strategy game has: a tank
   * that has survived three fights is *yours* in a way a freshly built one is
   * not, and keeping it alive becomes a goal the game never had to state. It
   * also rewards the thing good players already do — retreating damaged units
   * rather than feeding them in.
   *
   * Ranks are capped so a single veteran cannot snowball a match on its own,
   * and structures are excluded: a defence tower that ranks up by shooting
   * whatever walks past it defends itself for free.
   */
  private creditKill(shooter: number): void {
    if (!this.entities.isAlive(shooter)) return
    if (this.entities.has(shooter, EF.Structure)) return
    this.entities.kills[shooter]++

    const rank = this.entities.rank[shooter]
    if (rank >= MAX_RANK) return
    if (this.entities.kills[shooter] < KILLS_PER_RANK * (rank + 1)) return

    this.entities.rank[shooter] = rank + 1
    // Promotion heals, which is what makes a veteran feel earned rather than
    // merely labelled, and gives a damaged unit a reason to keep fighting.
    const maxHp = this.entities.maxHp[shooter]
    const healed = Math.floor((maxHp * RANK_HEAL) / 100)
    this.entities.hp[shooter] = Math.min(maxHp, this.entities.hp[shooter] + healed)
    this.ctx.emit('unit:promoted', {
      id: this.entities.idAt(shooter),
      owner: this.entities.owner[shooter],
      rank: rank + 1,
      x: this.entities.x[shooter],
      y: this.entities.y[shooter],
    })
  }

  /** Remove an entity and everything derived from it. */
  kill(slot: number): void {
    if (!this.entities.isAlive(slot)) return
    const id = this.entities.idAt(slot)
    const def = defById(this.entities.kind[slot])
    const owner = this.entities.owner[slot]
    const x = this.entities.x[slot]
    const y = this.entities.y[slot]

    if (def?.isStructure) {
      // Free every footprint cell back to passable (R17).
      const cell = this.world.cellAt(fix.raw(x), fix.raw(y))
      const cx = (cell % MAP_SIZE) - (def.width >> 1)
      const cy = ((cell / MAP_SIZE) | 0) - (def.height >> 1)
      this.world.freeFootprint(cx, cy, def.width, def.height)
      this.ctx.emit('world:passability', { cx, cy })
    }

    this.ordered[slot] = 0
    this.ctx.emit('actor:death', { id, kind: this.entities.kind[slot], owner, x, y })
    this.entities.destroy(id)

    // Elimination is checked on death rather than every tick — it can only
    // change when something dies, and the bot-match harness cannot terminate
    // without it.
    this.checkElimination(owner)
  }

  // --- win condition (R30) -------------------------------------------------

  /**
   * A player is eliminated when they hold no structures **and** no unit capable
   * of founding a base. Keeping combat units alive is not enough; neither is
   * holding an MCV with nowhere to put it, but that case is recoverable and
   * this one is not.
   */
  private checkElimination(player: number): void {
    if (player < 0 || player >= MAX_PLAYERS) return
    if (this.eliminated[player]) return

    let hasStructure = false
    let hasFounder = false
    const limit = this.entities.limit
    for (let s = 0; s < limit && !(hasStructure && hasFounder); s++) {
      if (!this.entities.isAlive(s)) continue
      if (this.entities.owner[s] !== player) continue
      if (this.entities.has(s, EF.Structure)) hasStructure = true
      if (this.entities.has(s, EF.Founder)) hasFounder = true
    }
    if (hasStructure || hasFounder) return

    this.eliminated[player] = 1
    this.ctx.emit('match:eliminated', { slot: player })
  }

  /** Players still in the match. */
  private survivors(): number[] {
    const alive: number[] = []
    for (let p = 0; p < MAX_PLAYERS; p++) {
      if (this.eliminated[p]) continue
      let any = false
      const limit = this.entities.limit
      for (let s = 0; s < limit && !any; s++) {
        if (this.entities.isAlive(s) && this.entities.owner[s] === p) any = true
      }
      if (any) alive.push(p)
    }
    return alive
  }

  private ended = false

  private checkMatchEnd(tick: number): void {
    if (this.ended) return
    const alive = this.survivors()
    if (alive.length === 1) {
      this.ended = true
      this.bridge.finishMatch(MATCH_WON, alive[0])
      return
    }
    if (alive.length === 0) {
      // Everyone gone in the same tick. Not a win, and not a draw either.
      this.ended = true
      this.bridge.finishMatch(MATCH_STALLED, -1)
      return
    }
    // Reaching the cap is a **stall**, reported distinctly from a win and
    // distinctly from a draw (R30, AE9).
    if (tick >= MATCH_TICK_CAP) {
      this.ended = true
      this.bridge.finishMatch(MATCH_STALLED, -1)
    }
  }

  get matchRunning(): boolean {
    return !this.ended
  }

  get status(): number {
    return this.ended ? MATCH_WON : MATCH_RUNNING
  }

  // --- superweapon (R31) ---------------------------------------------------

  fireSuperweapon(player: number, cx: number, cy: number): boolean {
    if (!this.build.isSuperweaponReady(player)) {
      this.ctx.emit('order:rejected', { owner: player, reason: REASON.NotCharged })
      return false
    }
    const kind = this.build.superKind[player]
    const def = defById(kind)
    if (!def || def.weapon === NO_WEAPON) return false

    this.build.consumeSuperweaponCharge(player)
    this.ctx.emit('super:fire', { owner: player, kind, x: cx, y: cy })

    // Resolved through the ordinary damage path — a superweapon needs no
    // bespoke damage code, only its own delivery hook and charge reset.
    this.applyWeapon(
      def.weapon,
      player,
      NO_ENTITY,
      this.world.cellCenterX(cx),
      this.world.cellCenterY(cy),
    )
    return true
  }

  /** Live projectiles, for the renderer and the profiler. */
  get projectileCount(): number {
    return this.projCount
  }

  /** Slots the renderer must scan to find them all. */
  get projectileCapacity(): number {
    return MAX_PROJECTILES
  }

  /** Read a projectile's position. Render-side only. */
  projectileAt(index: number): { x: number; y: number; active: boolean } {
    return {
      x: this.projX[index],
      y: this.projY[index],
      active: this.projActive[index] === 1,
    }
  }
}
